import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEndDayReport, getEndDayReportCustom } from '../../../api/report.api';
import styles from '../../../assets/scss/pages/ReportEndDay.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/formatters';

const PERIODS = [
    { key: 'today', label: 'Hôm nay' },
    { key: 'yesterday', label: 'Hôm qua' },
    { key: '7days', label: '7 ngày' },
    { key: 'thismonth', label: 'Tháng này' },
    { key: 'lastmonth', label: 'Tháng trước' },
    { key: 'custom', label: 'Tuỳ chọn' },
];

function formatDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

const EMPTY_DATA = {
    totalIncome: 0,
    totalExpense: 0,
    netIncome: 0,
    cashPayment: 0,
    bankTransfer: 0,
    pointsPayment: 0,
    discountAmount: 0,
    totalPointsUsed: 0,
    totalDiscount: 0,
    revenue: 0,
    totalAmount: 0,
    actualReceived: 0,
    totalOwed: 0,
    totalBills: 0,
};

export default function ReportEndDay() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.REPORT.END_DAY_VIEW);

    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('today');
    const [startDate, setStartDate] = useState(formatDate(new Date()));
    const [endDate, setEndDate] = useState(formatDate(new Date()));
    const [data, setData] = useState(EMPTY_DATA);

    const fetchData = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            let res;
            if (period === 'custom') {
                res = await getEndDayReportCustom({ startDate, endDate });
            } else {
                res = await getEndDayReport({ period });
            }
            setData({ ...EMPTY_DATA, ...res.data });
        } catch (err) {
            console.error('Error fetching end-day report:', err);
            setData(EMPTY_DATA);
        } finally {
            setLoading(false);
        }
    }, [canView, period, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        if (val > endDate) setEndDate(val);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        if (val < startDate) setStartDate(val);
    };

    const fc = (v) => formatCurrency(v || 0);

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </button>
                <h1 className={styles.title}>Báo cáo cuối ngày</h1>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.periodTabs}>
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            className={`${styles.periodTab}${period === p.key ? ` ${styles.periodTabActive}` : ''}`}
                            onClick={() => setPeriod(p.key)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {period === 'custom' && (
                    <div className={styles.customDateRow}>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Từ ngày:</span>
                            <input type="date" className={styles.dateInput} value={startDate} onChange={handleStartDateChange} />
                        </div>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Đến ngày:</span>
                            <input type="date" className={styles.dateInput} value={endDate} onChange={handleEndDateChange} />
                        </div>
                    </div>
                )}
            </div>

            {loading ? (
                <div className={styles.loading}>
                    <div className={styles.spinner} />
                    Đang tải dữ liệu...
                </div>
            ) : (
                <>
                    {/* Section 1: Tổng kết thu chi */}
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>Tổng kết thu chi</div>
                        <div className={styles.sectionBody}>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Tổng thu</span>
                                <span className={`${styles.rowValue} ${styles.rowValueBlue}`}>{fc(data.totalIncome)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Tổng chi</span>
                                <span className={`${styles.rowValue} ${styles.rowValueRed}`}>{fc(data.totalExpense)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Thu - Chi</span>
                                <span className={`${styles.rowValue} ${styles.rowValueGreen}`}>{fc(data.netIncome)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Phương thức thanh toán */}
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>Phương thức thanh toán</div>
                        <div className={styles.sectionBody}>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Tiền mặt</span>
                                <span className={styles.rowValue}>{fc(data.cashPayment)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Chuyển khoản</span>
                                <span className={styles.rowValue}>{fc(data.bankTransfer)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Điểm</span>
                                <span className={`${styles.rowValue} ${styles.rowValueRed}`}>- {fc(data.pointsPayment)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Giảm giá</span>
                                <span className={`${styles.rowValue} ${styles.rowValueRed}`}>- {fc(data.discountAmount)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>
                                    Tổng tiền giảm <span className={styles.rowLabelSub}>(Điểm + Giảm giá)</span>
                                </span>
                                <span className={`${styles.rowValue} ${styles.rowValueRed}`}>- {fc(data.totalDiscount)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Tổng kết bán hàng */}
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>Tổng kết bán hàng</div>
                        <div className={styles.sectionBody}>
                            <div className={`${styles.row} ${styles.rowClickable}`} onClick={() => {
                                if (period === 'custom') {
                                    navigate(`/admin/bills?startDate=${startDate}&endDate=${endDate}`);
                                } else {
                                    navigate(`/admin/bills?period=${period}`);
                                }
                            }}>
                                <span className={styles.rowLabel}>Hoá đơn</span>
                                <span className={`${styles.rowValue} ${styles.rowValueGreen}`}>
                                    {data.totalBills} <ChevronRight size={16} style={{ verticalAlign: -3, marginLeft: 2 }} />
                                </span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Doanh thu</span>
                                <span className={`${styles.rowValue} ${styles.rowValueBlue}`}>{fc(data.revenue)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Tổng cộng</span>
                                <span className={styles.rowValue}>{fc(data.totalAmount)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Tổng thực thu</span>
                                <span className={styles.rowValue}>{fc(data.actualReceived)}</span>
                            </div>
                            <div className={styles.row}>
                                <span className={styles.rowLabel}>Khách nợ</span>
                                <span className={`${styles.rowValue} ${styles.rowValueRed}`} style={{ fontWeight: 700 }}>
                                    {fc(data.totalOwed)}
                                </span>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
